/**
 * BodyMapView — shared body-map rendering surface used by BOTH the
 * ComprehensiveAnalysisRenderer (canonical BodyMapOutput) and Xoe's
 * BodyMapBlock (canonical or legacy flat XoeBodyTag[]).
 *
 * Visual appearance is identical to the former inline body-map section in
 * ComprehensiveAnalysisRenderer — the inline region-detail panel (NOT the
 * BodyTagDrawer) is used for click-to-reveal.
 *
 * Prop API:
 *   bodyMap     — full canonical output (front_tags + back_tags + anatomical_correlates)
 *   legacyTags  — flat XoeBodyTag[] for model-fallback when no analysis is available
 *   gender      — 'male' | 'female' (defaults to 'male')
 *
 * Exported helpers:
 *   comprehensiveTagToBodyTag  — maps ComprehensiveBodyTag → BodyTag (shared with renderers)
 *   mergeBilateralCorrelates   — merges bilateral anatomical correlates into single rows
 *   MergedCorrelate            — result type of the above
 */

import React, { useEffect, useState } from 'react';
import { Badge, Button, Select, Tag, Typography } from 'antd';

import HumanBodySvg from './HumanBodySvg';
import BodyModelLegend from './BodyModelLegend';
import { getBilateralBase, SVG_ID_LABELS } from './HumanBodySvg';

import type { BodyTag, TagCategory, TagSeverity, BodyRegion } from '../../../types/healthcare/BodyAnalysisTypes';
import type {
	BodyMapOutput,
	AnatomicalCorrelate,
	ComprehensiveBodyTag,
} from '../../../types/healthcare/PatientAnalysisTypes';
import type { XoeBodyTag } from '../../../types/xoe/XoeTypes';

const { Text } = Typography;

// ── Panel styles (mirror ComprehensiveAnalysisRenderer constants) ─────────────

const sectionLabel: React.CSSProperties = {
	fontSize: 10,
	fontWeight: 700,
	textTransform: 'uppercase',
	letterSpacing: '0.05em',
	color: 'var(--mg-text-secondary)',
	display: 'block',
	marginBottom: 6,
};

const panelStyle: React.CSSProperties = {
	border: '1px solid var(--mg-border)',
	borderRadius: 6,
	padding: '8px 10px',
	background: 'var(--mg-bg-card, var(--mg-bg))',
};

// ── MergedCorrelate ──────────────────────────────────────────────────────────

/** Anatomical correlate after bilateral merging (lung_left + lung_right → "Lungs"). */
export interface MergedCorrelate {
	key: string;
	label: string;
	severity: AnatomicalCorrelate['severity'];
	source_categories: AnatomicalCorrelate['source_categories'];
	clinical_summary: string;
}

const CORRELATE_SEVERITY_RANK: Record<AnatomicalCorrelate['severity'], number> = {
	critical: 0, warning: 1, info: 2, normal: 3,
};

// Stable group key for a correlate/tag region: bilateral organs collapse to their
// shared base so left + right merge; everything else keys on its own region.
function regionGroupKey(region: string): string {
	return getBilateralBase(region) ?? region;
}

/**
 * Merge anatomical correlates so bilateral organs (lungs, kidneys, …) appear as
 * a single joined entry instead of separate left/right rows.
 * Highest severity wins; source categories are unioned; distinct clinical summaries
 * are concatenated.
 * Exported so ComprehensiveAnalysisRenderer can import it from here instead of
 * maintaining a local copy.
 */
export function mergeBilateralCorrelates(correlates: AnatomicalCorrelate[]): MergedCorrelate[] {
	const groups = new Map<string, {
		label: string;
		severity: AnatomicalCorrelate['severity'];
		sources: Set<string>;
		summaries: Set<string>;
	}>();
	const order: string[] = [];

	for (const c of correlates) {
		const base = getBilateralBase(c.region);
		const key = base ?? c.region;
		let g = groups.get(key);
		if (!g) {
			g = {
				label: base
					? (SVG_ID_LABELS[base] ?? base.replace(/_/g, ' '))
					: c.region,
				severity: c.severity,
				sources: new Set(),
				summaries: new Set(),
			};
			groups.set(key, g);
			order.push(key);
		}
		if (CORRELATE_SEVERITY_RANK[c.severity] < CORRELATE_SEVERITY_RANK[g.severity]) {
			g.severity = c.severity;
		}
		c.source_categories.forEach((s) => g!.sources.add(s));
		if (c.clinical_summary) g!.summaries.add(c.clinical_summary);
	}

	return order.map((key) => {
		const g = groups.get(key)!;
		return {
			key,
			label: g.label,
			severity: g.severity,
			source_categories: Array.from(g.sources) as AnatomicalCorrelate['source_categories'],
			clinical_summary: Array.from(g.summaries).join(' '),
		};
	});
}

/**
 * Convert a canonical ComprehensiveBodyTag to a BodyTag suitable for HumanBodySvg.
 * Maps the 'info' severity (no longer a valid body-map severity) to 'normal'.
 * Exported so ComprehensiveAnalysisRenderer can import it from here.
 */
export function comprehensiveTagToBodyTag(tag: ComprehensiveBodyTag): BodyTag {
	return {
		// Server-stamped fields — carried through so the view can key on `id`, gate
		// practitioner-only affordances on `origin`, and surface `linked_record`.
		id:            tag.id,
		origin:        tag.origin,
		linked_record: tag.linked_record,
		region:   tag.region as BodyTag['region'],
		category: tag.category,
		// 'info' is no longer a body-map severity — fold any legacy value into normal.
		severity: tag.severity === 'info' ? 'normal' : tag.severity,
		title:    tag.title,
		detail:   tag.detail,
		source:   tag.source,
		evidence: tag.evidence,
	};
}

// ── Legacy coercion (Xoe flat XoeBodyTag[]) ───────────────────────────────────

const VALID_CATEGORIES: ReadonlySet<string> = new Set<TagCategory>([
	'blood', 'genetic', 'history', 'medication', 'imaging', 'forms',
]);

function coerceSeverity(raw: string): TagSeverity {
	switch (raw) {
		case 'low':      return 'normal';
		case 'medium':   return 'warning';
		case 'high':     return 'critical';
		// Pass through values already matching the BodyTag schema
		case 'normal':   return 'normal';
		case 'warning':  return 'warning';
		case 'critical': return 'critical';
		default:         return 'normal';
	}
}

function coerceXoeTag(xoeTag: XoeBodyTag): BodyTag {
	return {
		region:   xoeTag.region as BodyRegion,
		category: VALID_CATEGORIES.has(xoeTag.category)
			? (xoeTag.category as TagCategory)
			: 'history',
		severity: coerceSeverity(xoeTag.severity),
		title:    xoeTag.title,
		detail:   xoeTag.detail,
		...(xoeTag.source ? { source: xoeTag.source } : {}),
		// evidence omitted: XoeBodyTag.evidence is a string; BodyTag.evidence
		// expects a structured BodyTagEvidence object — omit to avoid type mismatch.
	};
}

// ── BodyMapView ───────────────────────────────────────────────────────────────

/**
 * An action a practitioner can trigger against a picked region from the inline
 * detail panel (e.g. "Add medical history", "Create test order"). When any are
 * supplied, untagged regions also become clickable and the panel gains a region
 * picker + one button per action.
 */
export interface BodyMapRegionAction {
	key: string;
	label: string;
	onSelect: (region: string, svgId: string, label: string) => void;
}

export interface BodyMapViewProps {
	/** Full canonical body-map output from a completed ComprehensiveAnalysis. */
	bodyMap?: BodyMapOutput;
	/** Flat Xoe body tags for model-fallback rendering when no analysis is available. */
	legacyTags?: XoeBodyTag[];
	/** Normalised patient sex/gender; defaults to 'male'. */
	gender?: 'male' | 'female';
	/** Practitioner region actions; when set, untagged regions become clickable. */
	regionActions?: BodyMapRegionAction[];
	/** Called when a practitioner edits their own (origin='practitioner') tag. */
	onTagEdit?: (tag: BodyTag) => void;
	/** Called when a practitioner deletes their own (origin='practitioner') tag. */
	onTagDelete?: (tag: BodyTag) => void;
}

const BodyMapView: React.FC<BodyMapViewProps> = ({ bodyMap, legacyTags, gender, regionActions, onTagEdit, onTagDelete }) => {
	const [bodyRegionDrawer, setBodyRegionDrawer] = useState<
		{ svgId: string; label: string; tags: BodyTag[]; regions: string[] } | null
	>(null);
	const [selectedRegion, setSelectedRegion] = useState<string | undefined>(undefined);

	const allowUntaggedRegionClick = !!regionActions?.length;

	// Default the region picker to the first region whenever the detail panel changes.
	useEffect(() => {
		setSelectedRegion(bodyRegionDrawer?.regions[0]);
	}, [bodyRegionDrawer]);

	// A practitioner's own tag is a clinical record — never hide it via the
	// normal-severity filter that suppresses low-signal AI findings.
	const isVisibleTag = (t: BodyTag) => t.origin === 'practitioner' || t.severity !== 'normal';

	// ── Compute rendered body tags ─────────────────────────────────────────────
	let allBodyTags: BodyTag[];
	let mergedCorrelates: MergedCorrelate[];

	if (bodyMap) {
		const frontTags = (bodyMap.front_tags ?? []).map(comprehensiveTagToBodyTag);
		const backTags  = (bodyMap.back_tags ?? []).map(comprehensiveTagToBodyTag);
		allBodyTags      = [...frontTags, ...backTags].filter(isVisibleTag);
		mergedCorrelates = mergeBilateralCorrelates(bodyMap.anatomical_correlates ?? [])
			.filter((c) => c.severity !== 'normal');
	} else {
		allBodyTags      = (legacyTags ?? []).map(coerceXoeTag).filter(isVisibleTag);
		mergedCorrelates = [];
	}

	const handleRegionClick = (svgId: string, label: string, tags: BodyTag[], regions: string[] = []) => {
		setBodyRegionDrawer({ svgId, label, tags, regions });
	};

	// No-data stub fires ONLY for the legacy/no-analysis path (no canonical bodyMap AND
	// no tags). When bodyMap IS present (real analysis), always render the SVG frame +
	// legend even if all arrays are empty — matching the original ComprehensiveAnalysis
	// renderer behaviour and avoiding a visual regression on that surface.
	// Region actions (practitioner tagging) likewise need the clickable body even with
	// no findings yet, so a patient who has never had an analysis can still be tagged.
	if (!bodyMap && allBodyTags.length === 0 && !allowUntaggedRegionClick) {
		return (
			<Text type="secondary" style={{ fontSize: 12 }}>
				No body map data to display.
			</Text>
		);
	}

	return (
		<div style={panelStyle}>
			<Text style={sectionLabel}>Body Region Analysis</Text>
			<div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
				{/* Combined body SVG (front + back tags merged) */}
				<div style={{ flexShrink: 0 }}>
					<HumanBodySvg
						gender={gender ?? 'male'}
						tags={allBodyTags}
						allowUntaggedRegionClick={allowUntaggedRegionClick}
						onRegionClick={handleRegionClick}
					/>
					{/* Severity colour legend (colours sourced from TAG_SEVERITY_COLORS) */}
					<div style={{ marginTop: 8 }}>
						<BodyModelLegend />
					</div>
				</div>

				{/* Anatomical correlates (canonical path only) */}
				{mergedCorrelates.length > 0 && (
					<div
						style={{
							flex: 1,
							maxHeight: 320,
							overflowY: 'auto',
							display: 'flex',
							flexDirection: 'column',
							gap: 4,
						}}
					>
						<Text style={sectionLabel}>Anatomical Correlates</Text>
						{mergedCorrelates.map((correlate) => {
							const severityColor =
								correlate.severity === 'critical' ? 'red'
								: correlate.severity === 'warning' ? 'orange'
								: 'green';
							const matchingTags = allBodyTags.filter(
								(t) => regionGroupKey(t.region) === correlate.key,
							);
							return (
								<div
									key={correlate.key}
									style={{
										border:       '1px solid var(--mg-border)',
										borderRadius: 4,
										padding:      '5px 8px',
										cursor:       matchingTags.length > 0 ? 'pointer' : 'default',
									}}
									onClick={() => {
										if (matchingTags.length > 0) {
											setBodyRegionDrawer({
												svgId:   correlate.key,
												label:   correlate.label,
												tags:    matchingTags,
												regions: Array.from(new Set(matchingTags.map((t) => t.region))),
											});
										}
									}}
								>
									<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 2 }}>
										<Badge color={severityColor} />
										<Text style={{ fontSize: 11, fontWeight: 600 }}>{correlate.label}</Text>
										<div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
											{correlate.source_categories.map((cat, ci) => (
												<Tag key={ci} style={{ margin: 0, fontSize: 9, padding: '0 4px' }}>
													{cat}
												</Tag>
											))}
										</div>
									</div>
									<Text
										style={{
											fontSize: 10,
											color: 'var(--mg-text-secondary)',
											display: 'block',
											lineHeight: 1.4,
										}}
									>
										{correlate.clinical_summary}
									</Text>
								</div>
							);
						})}
					</div>
				)}
			</div>

			{/* Inline region-detail panel — appears on region click or correlate click */}
			{bodyRegionDrawer && (
				<div
					style={{
						marginTop:    10,
						padding:      '8px 10px',
						borderRadius: 6,
						border:       '1px solid var(--mg-border)',
						background:   'var(--mg-bg-subtle)',
					}}
				>
					<div
						style={{
							display:        'flex',
							justifyContent: 'space-between',
							alignItems:     'center',
							marginBottom:   6,
						}}
					>
						<Text style={{ fontSize: 12, fontWeight: 600 }}>{bodyRegionDrawer.label}</Text>
						<Button size="small" type="text" onClick={() => setBodyRegionDrawer(null)}>
							Dismiss
						</Button>
					</div>
					{bodyRegionDrawer.tags.map((t, i) => (
						<div key={i} style={{ marginBottom: 5 }}>
							<Tag
								color={
									t.severity === 'critical' ? 'red'
									: t.severity === 'warning' ? 'orange'
									: 'green'
								}
								style={{ margin: 0, fontSize: 10 }}
							>
								{t.severity}
							</Tag>
							<Text style={{ marginLeft: 6, fontSize: 12, fontWeight: 600 }}>{t.title}</Text>
							{/* Edit/Delete affordances apply only to the practitioner's own tags. */}
							{t.origin === 'practitioner' && onTagEdit && (
								<Button
									size="small"
									type="text"
									style={{ fontSize: 11, height: 20, padding: '0 6px', marginLeft: 4 }}
									onClick={() => onTagEdit(t)}
								>
									Edit
								</Button>
							)}
							{t.origin === 'practitioner' && onTagDelete && (
								<Button
									size="small"
									type="text"
									danger
									style={{ fontSize: 11, height: 20, padding: '0 6px' }}
									onClick={() => onTagDelete(t)}
								>
									Delete
								</Button>
							)}
							<Text
								style={{
									display:    'block',
									fontSize:   11,
									color:      'var(--mg-text-secondary)',
									marginTop:  1,
								}}
							>
								{t.detail}
							</Text>
						</div>
					))}

					{/* Practitioner region actions — pick a region (when the element maps to more than one) then fire an action against it. */}
					{regionActions && regionActions.length > 0 && (
						<div
							style={{
								display:      'flex',
								alignItems:   'center',
								flexWrap:     'wrap',
								gap:          6,
								marginTop:    8,
								paddingTop:   8,
								borderTop:    '1px solid var(--mg-border)',
							}}
						>
							{bodyRegionDrawer.regions.length > 1 && (
								<>
									<Text style={{ fontSize: 11, fontWeight: 600 }}>Region</Text>
									<Select
										size="small"
										value={selectedRegion}
										onChange={setSelectedRegion}
										style={{ minWidth: 150 }}
										options={bodyRegionDrawer.regions.map((r) => ({
											value: r,
											label: r.replace(/_/g, ' '),
										}))}
									/>
								</>
							)}
							{regionActions.map((action) => (
								<Button
									key={action.key}
									size="small"
									onClick={() => action.onSelect(
										selectedRegion || bodyRegionDrawer.regions[0] || bodyRegionDrawer.svgId,
										bodyRegionDrawer.svgId,
										bodyRegionDrawer.label,
									)}
								>
									{action.label}
								</Button>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default BodyMapView;
